import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Loader2, CheckCircle, CheckSquare, Copy, Star } from 'lucide-react';

function QuoteItem(props) {
  var quote = props.quote;
  var onSuccess = props.onSuccess;
  var isHighlighted = props.isHighlighted;
  var onClearHighlight = props.onClearHighlight;
  
  var amountState = useState(quote.auto_calculated_quote || '');
  var amount = amountState[0];
  var setAmount = amountState[1];
  
  var messageState = useState('');
  var message = messageState[0];
  var setMessage = messageState[1];
  
  var respondingState = useState(false);
  var responding = respondingState[0];
  var setResponding = respondingState[1];
  
  var decliningState = useState(false);
  var declining = decliningState[0];
  var setDeclining = decliningState[1];
  
  var declineMessageState = useState('');
  var declineMessage = declineMessageState[0];
  var setDeclineMessage = declineMessageState[1];
  
  var showDeclineState = useState(false);
  var showDecline = showDeclineState[0];
  var setShowDecline = showDeclineState[1];
  
  var completingState = useState(false);
  var completing = completingState[0];
  var setCompleting = completingState[1];
  
  var reviewLinkState = useState(null);
  var reviewLink = reviewLinkState[0];
  var setReviewLink = reviewLinkState[1];
  
  var expandedState = useState(isHighlighted || false);
  var expanded = expandedState[0];
  var setExpanded = expandedState[1];
  
  var quoteRef = useRef(null);
  
  useEffect(function() {
    if (isHighlighted && quoteRef.current) {
      quoteRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      var timer = setTimeout(function() {
        if (onClearHighlight) onClearHighlight();
      }, 5000);
      return function() { clearTimeout(timer); };
    }
  }, [isHighlighted]);

  function handleSubmit() {
    setResponding(true);
    api.respondToQuote(quote.id, { quote_amount: parseFloat(amount), message: message }).then(function() {
      toast.success('Quote response sent!');
      onSuccess();
      setResponding(false);
    }).catch(function() {
      toast.error('Failed to send response');
      setResponding(false);
    });
  }
  
  function handleDecline() {
    setDeclining(true);
    api.declineQuote(quote.id, declineMessage || null).then(function(res) {
      toast.success(res.data.message);
      onSuccess();
      setDeclining(false);
      setShowDecline(false);
    }).catch(function(err) {
      toast.error(err.response?.data?.detail || 'Failed to decline');
      setDeclining(false);
    });
  }
  
  function handleMarkComplete() {
    setCompleting(true);
    api.markQuoteComplete(quote.id).then(function(res) {
      var link = window.location.origin + '/review/' + res.data.review_token;
      setReviewLink(link);
      toast.success('Job marked as complete!');
      onSuccess();
      setCompleting(false);
    }).catch(function(err) {
      toast.error(err.response?.data?.detail || 'Failed to mark complete');
      setCompleting(false);
    });
  }

  var addr = quote.service_address || {};
  var isCompleted = quote.status === 'completed' || quote.completed;
  var isAccepted = quote.status === 'accepted';
  var isDeclined = quote.status === 'declined';
  var isClosed = quote.status === 'closed';
  var isExpired = quote.status === 'expired';
  var needsAction = isAccepted && !isCompleted;
  
  var statusColor = 'bg-slate-500/20 text-slate-400 border-slate-500/30';
  if (quote.status === 'pending') statusColor = 'bg-amber-500/20 text-amber-400 border-amber-500/30';
  else if (quote.status === 'responded') statusColor = 'bg-blue-500/20 text-blue-400 border-blue-500/30';
  else if (quote.status === 'accepted') statusColor = 'bg-teal-500/20 text-teal-400 border-teal-500/30';
  else if (isCompleted) statusColor = 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30';
  else if (isDeclined) statusColor = 'bg-orange-500/20 text-orange-400 border-orange-500/30';
  else if (isExpired) statusColor = 'bg-red-500/20 text-red-400 border-red-500/30';

  var containerClass = "border rounded-lg p-4 transition-all duration-500 relative ";
  if (isHighlighted) containerClass += "border-teal-400 ring-2 ring-teal-400/50 bg-teal-500/10";
  else if (needsAction) containerClass += "border-teal-500/50 bg-teal-500/5 ring-1 ring-teal-500/30";
  else containerClass += "border-slate-700 bg-slate-800/40";

  return (
    <div ref={quoteRef} data-testid={'quote-item-' + quote.id} className={containerClass}>
      {needsAction && (
        <div className="absolute -top-2 -right-2 flex items-center gap-1.5">
          <span className="relative flex h-3 w-3">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-teal-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-3 w-3 bg-teal-500"></span>
          </span>
          <span className="text-[9px] font-bold text-teal-400 bg-teal-500/20 px-2 py-0.5 rounded-full tracking-wider">ACTION REQUIRED</span>
        </div>
      )}
      
      <div className="flex items-start justify-between mb-3">
        <div>
          <p className="font-medium text-white">{quote.client_name}</p>
          {quote.client_company && <p className="text-sm text-slate-500">{quote.client_company}</p>}
          <p className="text-xs text-slate-500 mt-1">{quote.client_email} • {quote.client_phone}</p>
        </div>
        <Badge className={statusColor + ' uppercase cursor-default text-[10px] font-bold tracking-wider'}>
          {isCompleted ? 'COMPLETED' : quote.status.toUpperCase()}
        </Badge>
      </div>
      
      <div className="grid sm:grid-cols-2 gap-2 text-sm text-slate-400 mb-3">
        <p><span className="text-slate-500">Location:</span> {addr.city}, {addr.state} {addr.zip}</p>
        <p><span className="text-slate-500">Document:</span> {quote.document_type}</p>
        <p><span className="text-slate-500">Urgency:</span> <span className={quote.urgency === 'same_day' ? 'text-red-400' : quote.urgency === 'rush' ? 'text-amber-400' : ''}>{quote.urgency}</span></p>
        <p><span className="text-slate-500"># of Serves:</span> {quote.num_serves || 1}</p>
      </div>
      
      <button onClick={function() { setExpanded(!expanded); }} className="text-xs text-teal-400 hover:text-teal-300 mb-3" data-testid={'quote-expand-' + quote.id}>
        {expanded ? '▼ Hide Details' : '▶ Show Full Details'}
      </button>
      
      {expanded && (
        <div className="mb-3 p-3 bg-slate-900/40 rounded-lg text-sm space-y-2">
          <p className="text-slate-400">
            <span className="text-slate-500">Full Address:</span> {addr.street}, {addr.city}, {addr.state} {addr.zip}
            {addr.verified && <span className="ml-2 text-emerald-400 text-xs">✓ Verified</span>}
          </p>
          {quote.additional_details && <p className="text-slate-400"><span className="text-slate-500">Notes:</span> {quote.additional_details}</p>}
          
          {quote.client_calculated_total ? (
            <div className="pt-2 border-t border-slate-800">
              <p className="text-slate-400"><span className="text-slate-500">Client Estimate:</span><span className="text-teal-400 font-mono font-bold ml-2">${quote.client_calculated_total.toFixed(2)}</span></p>
              {quote.fee_breakdown && quote.fee_breakdown.length > 0 && (
                <div className="mt-2 pl-3 border-l-2 border-slate-700 space-y-1">
                  {quote.fee_breakdown.map(function(item, i) {
                    return <p key={i} className="text-xs"><span className="text-slate-500">{item.label}:</span><span className="text-teal-400 ml-1">${item.amount.toFixed(2)}</span>{item.detail && <span className="text-slate-600 ml-1">({item.detail})</span>}</p>;
                  })}
                </div>
              )}
            </div>
          ) : (
            <p className="text-slate-400"><span className="text-slate-500">Auto Quote:</span><span className="text-teal-400 font-mono ml-2">${quote.auto_calculated_quote}</span></p>
          )}
          
          {quote.guaranteed_response_hours && <p className="text-slate-400"><span className="text-slate-500">Response Guarantee:</span><span className="text-amber-400 ml-2">{quote.guaranteed_response_hours}h</span></p>}
          
          <div className="pt-2 border-t border-slate-800 space-y-1 text-xs text-slate-500">
            <p>Requested: {new Date(quote.created_at).toLocaleString()}</p>
            {quote.response && quote.response.responded_at && <p>Responded: {new Date(quote.response.responded_at).toLocaleString()}</p>}
            {quote.accepted_at && <p>Accepted: {new Date(quote.accepted_at).toLocaleString()}</p>}
            {quote.completed_at && <p>Completed: {new Date(quote.completed_at).toLocaleString()}</p>}
          </div>
        </div>
      )}
      
      {quote.status === 'pending' && !showDecline && (
        <div className="pt-3 border-t border-slate-700 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Input type="number" placeholder="Your quote ($)" value={amount} onChange={function(e) { setAmount(e.target.value); }} className="bg-slate-900/60 border-slate-700 text-white" />
            <Input placeholder="Message (optional)" value={message} onChange={function(e) { setMessage(e.target.value); }} className="bg-slate-900/60 border-slate-700 text-white" />
          </div>
          <div className="flex gap-2">
            <Button onClick={handleSubmit} className="flex-1 btn-accent" disabled={responding}>{responding && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Send Quote</Button>
            <Button onClick={function() { setShowDecline(true); }} variant="outline" className="border-slate-600 text-slate-400">Decline</Button>
          </div>
        </div>
      )}
      
      {quote.status === 'pending' && showDecline && (
        <div className="pt-3 border-t border-slate-700 space-y-3">
          <p className="text-sm text-slate-400">Decline this quote?</p>
          <Input placeholder="Reason (optional)" value={declineMessage} onChange={function(e) { setDeclineMessage(e.target.value); }} className="bg-slate-900/60 border-slate-700 text-white" />
          <div className="flex gap-2">
            <Button onClick={function() { setShowDecline(false); }} variant="outline" className="flex-1 border-slate-600">Cancel</Button>
            <Button onClick={handleDecline} className="flex-1 bg-red-500 hover:bg-red-600" disabled={declining}>{declining && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Confirm</Button>
          </div>
        </div>
      )}
      
      {quote.status === 'responded' && quote.response && !isCompleted && (
        <div className="pt-3 border-t border-slate-700 space-y-3">
          <p className="text-sm text-blue-400"><CheckCircle className="h-4 w-4 inline mr-1" />Quoted: ${quote.response.quote_amount}</p>
          <Button onClick={handleMarkComplete} variant="outline" className="w-full border-emerald-500/30 text-emerald-400" disabled={completing}>{completing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckSquare className="h-4 w-4 mr-2" />}Mark Complete</Button>
        </div>
      )}
      
      {quote.status === 'accepted' && !isCompleted && (
        <div className="pt-3 border-t border-slate-700 space-y-3">
          <p className="text-sm text-teal-400"><CheckCircle className="h-4 w-4 inline mr-1" />Accepted! ${quote.response?.quote_amount}</p>
          <Button onClick={handleMarkComplete} variant="outline" className="w-full border-emerald-500/30 text-emerald-400" disabled={completing}>{completing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckSquare className="h-4 w-4 mr-2" />}Mark Complete</Button>
        </div>
      )}
      
      {isCompleted && (
        <div className="pt-3 border-t border-slate-700 space-y-3">
          <p className="text-sm text-emerald-400"><CheckSquare className="h-4 w-4 inline mr-1" />Job Completed</p>
          {(reviewLink || quote.review_token) && !quote.review_submitted && (
            <div className="p-3 bg-slate-900/60 rounded-lg">
              <p className="text-xs text-slate-400 mb-2">Share for review:</p>
              <div className="flex gap-2">
                <Input readOnly value={reviewLink || (window.location.origin + '/review/' + quote.review_token)} className="bg-slate-800 border-slate-700 text-white text-xs flex-1" />
                <Button size="sm" variant="outline" onClick={function() { navigator.clipboard.writeText(reviewLink || (window.location.origin + '/review/' + quote.review_token)); toast.success('Copied!'); }} className="border-slate-700"><Copy className="h-4 w-4" /></Button>
              </div>
            </div>
          )}
          {quote.review_submitted && <p className="text-sm text-slate-500"><Star className="h-4 w-4 inline mr-1" />Review received</p>}
        </div>
      )}
      
      {isDeclined && <div className="pt-3 border-t border-slate-700"><p className="text-sm text-orange-400">Client selected another server</p></div>}
      {isClosed && <div className="pt-3 border-t border-slate-700"><p className="text-sm text-slate-400">Quote closed</p></div>}
      {isExpired && <div className="pt-3 border-t border-slate-700"><p className="text-sm text-red-400">Quote expired</p></div>}
    </div>
  );
}

export default QuoteItem;
